import { useEffect, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../stores/hooks/hooks.ts";
import { PersonaFormData } from "../../../../../../stores/types/personasTypes.tsx";
import { PersonaForm } from "./components/PersonaForm.tsx";
import {
  createPersonaThunk,
  fetchCoreValues,
} from "../../../../../../stores/thunks/personasThunk.tsx";
import {
  RevButton,
  RevIconButton,
  RevLink,
  RevSlideout,
  RevSpinner,
  RevUnsavedChangesDialog,
} from "@revmatics/catalyst";
import { faXmark } from "@fortawesome/pro-light-svg-icons";

type Props = {
  isOpen: boolean;
  setIsOpen: () => void;
};

export const CreatePersona = ({ isOpen, setIsOpen }: Props) => {
  const { personaDetails: ffPersonaDetails } = useFlags();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [href, setHref] = useState("");
  const [isOpenUnsavedModal, setOpenUnsavedModal] = useState(false);
  const [hasUnsavedChanges, setUnsavedChanges] = useState(false);
  const { coreValues, isLoadingCoreValues } = useAppSelector(
    (state) => state.personas,
  );

  const methods = useForm<PersonaFormData>({
    mode: "onSubmit",
    defaultValues: {
      name: "",
      needs: "",
      concerns: "",
      motivations: "",
      age_from: "",
      age_to: "",
      gender: "All",
      income_from: "",
      income_to: "",
      education: "",
      occupation: "",
      marital_status: "All",
      family_size: "All",
      locations: { included_locations: [], excluded_locations: [] },
      interests: [],
      preferred_browsing_method: "All",
      preferred_buying_method: "All",
      extroverted_introverted: 4,
      serious_playful: 4,
      active_sedentary: 4,
      follower_trendsetter: 4,
      spender_saver: 4,
      is_ai_generated: false,
      is_platform: false,
      persona_picture: null,
      persona_picture_url: null,
      main_core_value: "",
      secondary_core_value: "",
    },
  });

  const { handleSubmit, setError, clearErrors, reset } = methods;

  useEffect(() => {
    if (!coreValues && isOpen) {
      dispatch(fetchCoreValues());
    }
  }, [dispatch, coreValues, isOpen]);

  const handleDiscardChanges = () => {
    setOpenUnsavedModal(false);
    if (href) {
      navigate(href);
    } else {
      navigate(-1);
    }
    setHref("");
  };

  const onSubmit: SubmitHandler<PersonaFormData> = async (data) => {
    if (Number(data.income_from) > Number(data.income_to)) {
      setError("income_from", {
        type: "custom",
        message: "'From' cannot be greater than 'To'",
      });
      return;
    }
    setIsSubmitting(true);
    clearErrors("root.serverError");

    const includedLocationIds =
      data.locations?.included_locations.map((loc) => loc.id) || [];
    const excludedLocationIds =
      data.locations?.excluded_locations.map((loc) => loc.id) || [];

    const apiDataPayload = {
      ...data,
      included_locations: includedLocationIds,
      excluded_locations: excludedLocationIds,
    };

    delete apiDataPayload.locations;

    const formDataForApi = new FormData();

    delete apiDataPayload.main_core_value;
    delete apiDataPayload.secondary_core_value;
    const coreValuesPayload = [];

    if (data.main_core_value) {
      coreValuesPayload.push({
        core_value_id: Number(data.main_core_value),
        rank: 1,
      });
    }

    if (data.secondary_core_value) {
      coreValuesPayload.push({
        core_value_id: Number(data.secondary_core_value),
        rank: 2,
      });
    }

    if (coreValuesPayload.length) {
      coreValuesPayload.forEach((item, index) => {
        formDataForApi.append(
          `core_values[${index}]core_value_id`,
          String(item.core_value_id),
        );
        formDataForApi.append(`core_values[${index}]rank`, String(item.rank));
      });
    }

    Object.entries(apiDataPayload).forEach(([key, value]) => {
      if (key === "persona_picture" && value instanceof File) {
        formDataForApi.append("persona_picture", value);
      } else if (Array.isArray(value) && key !== "core_values") {
        value.forEach((item) => formDataForApi.append(key, String(item)));
      } else if (value !== undefined && value !== null) {
        formDataForApi.append(key, String(value));
      }
    });
    dispatch(
      createPersonaThunk({
        data: formDataForApi,
        callback: () => {
          reset();
          if (href && hasUnsavedChanges) {
            navigate(href);
          }
        },
      }),
    )
      .unwrap()
      .then((createdPersona) => {
        if (ffPersonaDetails) {
          navigate(`/personas?personaId=${createdPersona.id}`);
        } else {
          navigate(`/personas`);
        }
      })
      .catch((error: unknown) => {
        console.error("Failed to create persona:", error);
        let message = "Failed to create persona.";
        if (typeof error === "string") message = error;
        else if (error && typeof error === "object") {
          const errorMessages = Object.entries(error).map(
            ([field, errors]) => `${field}: ${(errors as string[]).join(", ")}`,
          );
          message = errorMessages.join("; ");
        }
        setError("root.serverError", { type: "server", message });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <RevSlideout
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      size="lg"
      headerComponent={
        <div className="flex justify-between px-4 py-6 sm:px-6 border-b border-monochrome-100">
          <h5>Create Persona</h5>
          <RevIconButton
            icon={faXmark}
            variant="outline"
            size="l"
            handleClick={setIsOpen}
            ariaLabel="Close slideout"
          />
        </div>
      }
    >
      {isLoadingCoreValues ? (
        <div className="h-full">
          <RevSpinner size="large" color="primary" centered />
        </div>
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-primary-25 pt-4 pb-8 px-6">
              <PersonaForm
                setHref={setHref}
                setOpenUnsavedModal={setOpenUnsavedModal}
                hasUnsavedChanges={hasUnsavedChanges}
                setUnsavedChanges={setUnsavedChanges}
              />
              {methods.formState.errors.root?.serverError && (
                <p className="text-sm text-red-500 mt-2">
                  {methods.formState.errors.root.serverError.message}
                </p>
              )}
              <RevUnsavedChangesDialog
                pageId="profile-settings-page"
                isOpen={isOpenUnsavedModal}
                setIsOpen={setOpenUnsavedModal}
                onSave={handleSubmit(onSubmit)}
                onDiscard={handleDiscardChanges}
                isLoading={isSubmitting}
                classNameContainer="z-50"
              />
            </div>
            <div className="grid grid-cols-2 sm:flex sm:justify-end gap-3 sticky bottom-0 px-6 py-4 bg-white shadow-elevation--1 z-10">
              <RevLink
                displayAs="button"
                text="Cancel"
                buttonVariant="secondary"
                buttonSize="medium"
                pathname="/personas"
              />
              <RevButton
                type="submit"
                text="Save"
                buttonSize="medium"
                variant="primary"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              />
            </div>
          </form>
        </FormProvider>
      )}
    </RevSlideout>
  );
};
